import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * DesktopPrimaryRail - Row 1 of the desktop rail navigation
 * Handles sport/league tab selection with horizontal scrolling
 * Adapted from mobile PrimaryRail for desktop interface
 *
 * @param {Array} tabs - Array of tab objects with id, label, icon
 * @param {string} activeTab - Currently active tab id
 * @param {Function} onTabChange - Callback when tab is selected
 * @param {string} variant - 'sport' or 'league' styling variant
 */
const DesktopPrimaryRail = ({
  tabs = [],
  activeTab,
  onTabChange,
  variant = 'sport'
}) => {
  const scrollContainerRef = useRef(null);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // Check scroll position for gradient indicators
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [tabs]);

  // Auto-scroll to active tab
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !activeTab) return;

    const activeElement = container.querySelector(`[data-tab-id="${activeTab}"]`);
    if (activeElement) {
      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();

      // Center the active tab if possible
      const scrollLeft = elementRect.left - containerRect.left
        + container.scrollLeft
        - (containerRect.width / 2)
        + (elementRect.width / 2);

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }, [activeTab]);

  return (
    <div className="relative bg-black/10 backdrop-blur-sm">
      {/* Left gradient indicator */}
      {showLeftGradient && (
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-black/40 to-transparent z-10 pointer-events-none" />
      )}

      {/* Right gradient indicator */}
      {showRightGradient && (
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-black/40 to-transparent z-10 pointer-events-none" />
      )}

      {/* Scrollable tab container - Desktop optimized */}
      <div
        ref={scrollContainerRef}
        className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide scroll-smooth"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <motion.button
              key={tab.id}
              data-tab-id={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                relative flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap
                transition-all duration-200 min-h-[36px] font-medium text-sm
                ${isActive
                  ? 'bg-emerald-500/90 text-white shadow-lg shadow-emerald-500/20'
                  : 'text-white/60 hover:bg-white/10 hover:text-white/80'
                }
              `}
              whileTap={{ scale: 0.98 }}
              aria-selected={isActive}
              role="tab"
            >
              {Icon && <Icon size={16} />}
              <span>{tab.label}</span>

              {/* Badge for count */}
              {tab.count && (
                <span className={`
                  ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-white/10 text-white/60'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default DesktopPrimaryRail;
